import {
  Button,
  Chip,
  HStack,
  Input,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import useCreateUserFeedback from 'api/healthCoach/mutations/useCreateUserFeedback';
import {
  FEEDBACK_OPTIONS,
  FEEDBACK_OPTION_SENTIMENT,
  FEEDBACK_TYPE,
  FeedbackOption,
  SENTIMENT_CHIP_VARIANT,
} from 'models/userFeedback';
import { useEffect, useState } from 'react';

const symptomsFeedbackOptions = [
  FEEDBACK_OPTIONS.majorImprovement,
  FEEDBACK_OPTIONS.noticeableImprovement,
  FEEDBACK_OPTIONS.noChange,
  FEEDBACK_OPTIONS.worsening,
];

const implementationFeedbackOptions = [
  FEEDBACK_OPTIONS.successful,
  FEEDBACK_OPTIONS.challenges,
];

const FeedbackSection = ({
  title,
  options,
  selectedOption,
  selectOption,
}: {
  title: string;
  options:
    | typeof symptomsFeedbackOptions
    | typeof implementationFeedbackOptions;
  selectedOption: FeedbackOption | null;
  selectOption: (option: FeedbackOption) => void;
}) => {
  return (
    <>
      <Text fontSize={'xs'} fontWeight={600} color={'seafoam'}>
        {title}
      </Text>
      <HStack
        justifyContent={'flex-start'}
        gap={2}
        mt={'auto'}
        maxW={'100%'}
        flexWrap={'wrap'}
      >
        {options.map((option: FeedbackOption) => {
          const selected = selectedOption === option;
          const sentiment = FEEDBACK_OPTION_SENTIMENT[option];

          return (
            <Chip
              size={'lg'}
              h={'30px'}
              px={4}
              fontWeight={selected ? 600 : 400}
              onClick={() => selectOption(option)}
              variant={
                selected ? SENTIMENT_CHIP_VARIANT[sentiment] : 'neutral'
              }
              key={option}
            >
              {option}
            </Chip>
          );
        })}
      </HStack>
    </>
  );
};

type Props = {
  blockId: string;
};

export const BlockFeedbackForm = ({ blockId }: Props) => {
  const [symptomFeedback, setSymptomFeedback] = useState<FeedbackOption | null>(
    null
  );
  const [implementationFeedback, setImplementationFeedback] =
    useState<FeedbackOption | null>(null);
  const [notes, setNotes] = useState<string | null>(null);

  const { mutation: createUserFeedback } = useCreateUserFeedback();

  const showBackButton =
    !!symptomFeedback || !!implementationFeedback || !!notes;

  useEffect(() => {
    if (createUserFeedback.isSuccess) {
      setSymptomFeedback(null);
      setImplementationFeedback(null);
      setNotes(null);
    }
  }, [createUserFeedback.isSuccess]);

  const selectSymptomFeedback = (option: FeedbackOption) => {
    setSymptomFeedback((prevValue) => (option === prevValue ? null : option));
  };

  const selectImplementationFeedback = (option: FeedbackOption) => {
    setImplementationFeedback((prevValue) =>
      option === prevValue ? null : option
    );
  };

  const handleSubmit = async () => {
    await createUserFeedback.mutateAsync({
      user_block_id: blockId,
      survey: {
        symptoms_feedback: symptomFeedback,
        implementation_feedback: implementationFeedback,
        notes: notes,
      },
      feedback_day: new Date().toISOString().split('T')[0],
      survey_type: FEEDBACK_TYPE.CAREOS_BLOCK_FEEDBACK,
    });
  };

  return (
    <VStack
      p={4}
      borderTopWidth={1}
      flexShrink={0}
      boxShadow={'0 -4px 16px 8px rgba(0, 0, 0, 0.05)'}
    >
      <VStack gap={3} mb={2}>
        <FeedbackSection
          title={'Symptoms Feedback'}
          options={symptomsFeedbackOptions}
          selectedOption={symptomFeedback}
          selectOption={selectSymptomFeedback}
        />
        <FeedbackSection
          title={'Implementation Feedback'}
          options={implementationFeedbackOptions}
          selectOption={selectImplementationFeedback}
          selectedOption={implementationFeedback}
        />

        <Input
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes"
          value={notes || ''}
          rounded={'full'}
        />
      </VStack>
      {showBackButton && (
        <Button
          loading={createUserFeedback.isLoading}
          onClick={handleSubmit}
          disabled={!symptomFeedback && !implementationFeedback && !notes}
        >
          Add member-reported feedback
        </Button>
      )}
    </VStack>
  );
};

export default BlockFeedbackForm;
